// these hooks are complicated, its still in flux
/* eslint-disable no-unused-vars */

import { useSnackbar } from 'notistack';
import { useCallback } from 'react';
import { addEdge } from 'reactflow';
import { isDesignCanvasProject } from '../../../config/designMode';
import { useAuth } from '../../../contexts/Auth';
import useApi from '../../../hooks/useAPI';
import {
  evaluateConnection,
  getHandlePropertySchema,
} from '../../ProjectPackage/utils/validate';
import { portTypeKey } from '../utils/portType';

const useEdgeOperations = (projectId, projectData, nodes, edges, setEdges) => {
  const { token } = useAuth();
  const { projects: projectsApi } = useApi();
  const { enqueueSnackbar } = useSnackbar();

  const evaluateParams = useCallback(
    (source, target, sourceHandle, targetHandle) => {
      const sourceNode = nodes.find((node) => node.id === source);
      const targetNode = nodes.find((node) => node.id === target);
      return evaluateConnection({
        sourceNode,
        targetNode,
        sourceHandle,
        targetHandle,
      });
    },
    [nodes]
  );

  const validateConnection = useCallback(
    (source, target, sourceHandle, targetHandle) =>
      evaluateParams(source, target, sourceHandle, targetHandle).valid,
    [evaluateParams]
  );

  const onConnect = useCallback(
    async (params) => {
      const result = evaluateParams(
        params.source,
        params.target,
        params.sourceHandle,
        params.targetHandle
      );
      if (!result.valid) {
        enqueueSnackbar(result.reason || 'Invalid connection', {
          variant: 'warning',
        });
        return;
      }

      const sourceNode = nodes.find((node) => node.id === params.source);
      const targetNode = nodes.find((node) => node.id === params.target);
      const sourceSchema = getHandlePropertySchema(
        sourceNode?.data?.outputs,
        params.sourceHandle
      );
      const newConnection = {
        source_package_id: params.source,
        target_package_id: params.target,
        source_handle: params.sourceHandle,
        target_handle: params.targetHandle,
      };
      try {
        const response = await projectsApi.createConnection(
          projectId,
          newConnection,
          token
        );
        if (response && response.body) {
          const newEdge = {
            ...response.body,
            data: {
              connectionType: portTypeKey(sourceSchema),
              targetDeployStatus: targetNode?.data?.deploy_status,
              onDelete: handleEdgeDelete,
            },
          };
          setEdges((eds) => addEdge(newEdge, eds));
        }
      } catch (error) {
        console.error('Error saving connection:', error);
        enqueueSnackbar('Failed to save connection', { variant: 'error' });
      }
    },
    [
      projectId,
      nodes,
      token,
      projectsApi,
      setEdges,
      evaluateParams,
      enqueueSnackbar,
    ]
  );

  const handleEdgeDelete = useCallback(
    async (edgeId) => {
      try {
        const edge = edges.find((e) => e.id === edgeId);
        if (edge) {
          if (!isDesignCanvasProject(projectId)) {
            await projectsApi.deleteConnection(
              projectId,
              edge.source,
              edge.target,
              token
            );
          }
          setEdges((eds) => eds.filter((e) => e.id !== edgeId));
        }
      } catch (error) {
        console.error('Error deleting connection:', error);
      }
    },
    [projectId, edges, token, projectsApi, setEdges]
  );

  const onConnectStart = useCallback(() => {
    // Add any logic needed when a connection starts
  }, []);

  const onConnectEnd = useCallback(() => {
    // Add any logic needed when a connection ends
  }, []);

  /** React Flow `isValidConnection` — reject out→out / schema mismatches while dragging. */
  const onConnectCheck = useCallback(
    (connection) => {
      return validateConnection(
        connection.source,
        connection.target,
        connection.sourceHandle,
        connection.targetHandle
      );
    },
    [validateConnection]
  );

  return {
    edges,
    setEdges,
    // onEdgesChange,
    onConnect,
    onConnectStart,
    onConnectEnd,
    onConnectCheck,
    handleEdgeDelete,
    validateConnection,
  };
};

export default useEdgeOperations;
